import React, { useState, useEffect } from 'react';
import PageHeader from '../../components/PageHeader';
import { 
    Box, 
    Button, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    FormControl, 
    MenuItem, 
    Select, 
    Stack, 
    TextField, 
    Typography,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    CircularProgress,
    IconButton,
    Menu,
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    PlayArrow as PlayArrowIcon,
    Download as DownloadIcon,
    MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import VideoPlayerModal from '../../components/VideoPlayerModal';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { httpService } from '../../services/httpService';
import { useSelector } from 'react-redux';
import { decryptResponse, encryptResponse, getSessionKey } from '../../services';

interface CallRecording {
    _id: string;
    room_id: string;
    call_start_time: string;
    call_duration: string;
    call_mode: string;
    call_status: string;
    final_output_file: string;
    call_type: string;
}

interface PaginationInfo {
    current_page: number;
    limit: number;
    total_records: number;
    total_pages: number;
    has_next_page: boolean;
    has_previous_page: boolean;
}

const CallRecordingsScreen: React.FC = () => {

    const [selectedApplicationId, setSelectedApplicationId] = useState<string>('all_apps');
    const [applications, setApplications] = useState<any[]>([{ appId: 'all_apps', appName: 'All Applications' }]);
    const [dateRange, setDateRange] = useState('last7days');
    const [customDateOpen, setCustomDateOpen] = useState(false);
    const [tempFromDate, setTempFromDate] = useState<Date | null>(null);
    const [tempToDate, setTempToDate] = useState<Date | null>(null);
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);
    const [recordings, setRecordings] = useState<CallRecording[]>([]);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [isLoadingRecordings, setIsLoadingRecordings] = useState<boolean>(false);
    const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
    const [videoPlayerOpen, setVideoPlayerOpen] = useState<boolean>(false);
    const [selectedVideoUrl, setSelectedVideoUrl] = useState<string>('');
    const [selectedVideoTitle, setSelectedVideoTitle] = useState<string>('');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedRecording, setSelectedRecording] = useState<CallRecording | null>(null);
    const userDetails = useSelector((state: any) => state.auth.userDetails);

    useEffect(() => {
        const range = getDateRange('last7days');
        setFromDate(range.from);
        setToDate(range.to);
    }, []);

    useEffect(() => {
        fetchApplications();
    }, [userDetails]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchCallRecordings();
        }
    }, [fromDate, toDate, selectedApplicationId, page, rowsPerPage]);

    const handleDateRangeChange = (value: string) => {
        setDateRange(value);
        setPage(0);
        if (value === 'custom') {
            setCustomDateOpen(true);
            setTempFromDate(fromDate);
            setTempToDate(toDate);
        } else {
            const range = getDateRange(value);
            setFromDate(range.from);
            setToDate(range.to);
        }
    };

    const handleCustomDateCancel = () => {
        setTempFromDate(fromDate);
        setTempToDate(toDate);
        setCustomDateOpen(false);
        setDateRange(fromDate && toDate ? 'custom' : 'last7days');
    };

    const handleCustomDateApply = () => {
        setFromDate(tempFromDate);
        setToDate(tempToDate);
        setCustomDateOpen(false);
        setPage(0);
    };

    const getCurrentDateRange = () => {
        let rangeFrom = fromDate;
        let rangeTo = toDate;

        if (dateRange !== 'custom') {
            const range = getDateRange(dateRange);
            rangeFrom = range.from;
            rangeTo = range.to;
        }

        if (!rangeFrom || !rangeTo) return '';

        const formatDate = (date: Date) => {
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        };

        if (rangeFrom.toDateString() === rangeTo.toDateString()) {
            return formatDate(rangeFrom);
        }
        return `${formatDate(rangeFrom)} - ${formatDate(rangeTo)}`;
    };

    const getDateRange = (option: string) => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        switch (option) {
            case 'last7days':
                const last7days = new Date(today);
                last7days.setDate(last7days.getDate() - 7);
                return { from: last7days, to: today };
            case 'last14days':
                const last14days = new Date(today);
                last14days.setDate(last14days.getDate() - 14);
                return { from: last14days, to: today };
            case 'last4weeks':
                const last4weeks = new Date(today);
                last4weeks.setDate(last4weeks.getDate() - 28);
                return { from: last4weeks, to: today };
            default:
                return { from: fromDate, to: toDate };
        }
    };

    const fetchApplications = async () => {
        const payload = {
            request_id: "notegtest1",
            timestamp: new Date().toISOString(),
            action: "get_all_applications",
            data: await encryptResponse({
                organization_id: userDetails?.organization_id || '',
                user_id: userDetails?.user_id || '',
            }, REQ_ENC_KEY)
        };
        try {
            const response = await httpService.post<any>(`${API_BASE_URL}/user/getAllApplications`, payload);
            if (response.data.status === 200 && response.data.data) {
                const data = response.data.data;
                const decrypted = await decryptResponse<{
                    applications: any[];
                }>(data, getSessionKey() as string);
                const { applications } = decrypted;
                const fetchedApplications = applications || [];
                const appData = fetchedApplications.map((app: any) => ({ appId: app.app_id, appName: app.app_name }));
                const updatedApplications = [{ appId: 'all_apps', appName: 'All Applications' }, ...appData];
                setApplications(updatedApplications);
            } else {
                setApplications([{ appId: 'all_apps', appName: 'All Applications' }]);
            }
        } catch (error) {
            console.error('Error fetching applications:', error);
            setApplications([{ appId: 'all_apps', appName: 'All Applications' }]);
        }
    };

    const formatDateForAPI = (date: Date | null): string => {
        if (!date) return '';
        return date.toISOString().split('T')[0];
    };

    const fetchCallRecordings = async (): Promise<void> => {
        if (!userDetails?.organization_id || !fromDate || !toDate) return;

        setIsLoadingRecordings(true);
        try {
            
            const payload = {
                data: await encryptResponse({
                    organization_id: userDetails.organization_id,
                    app_id: selectedApplicationId,
                    from: formatDateForAPI(fromDate),
                    to: formatDateForAPI(toDate),
                    page: page + 1,
                    limit: rowsPerPage
                }, REQ_ENC_KEY)
            };

            const response = await httpService.post<any>(
                `${API_BASE_URL}/analytics/getCallRecordingsData`,
                payload
            );

            if (response.data.status === 200 && response.data.data) {
                const data = response.data.data;
                const decrypted = await decryptResponse<{
                    result: {
                        recordings: CallRecording[];
                        pagination: PaginationInfo;
                        summary: any;
                    };
                }>(data, getSessionKey() as string);
                const { recordings, pagination } = decrypted.result || {};
                setRecordings(recordings || []);
                setPaginationInfo(pagination || null);
            } else {
                setRecordings([]);
                toast.error(response.data.message || 'Failed to fetch call recordings', {
                    position: 'top-right',
                    autoClose: 3000
                });
            }
        } catch (error) {
            console.error('Error fetching call recordings:', error);
            setRecordings([]);
            toast.error('An error occurred while fetching call recordings', {
                position: 'top-right',
                autoClose: 3000
            });
        } finally {
            setIsLoadingRecordings(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number): void => setPage(newPage);

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handlePlayRecording = (recordingUrl: string, roomId: string, callType: string): void => {
        setSelectedVideoUrl(recordingUrl);
        setSelectedVideoTitle(`Room ${roomId} - ${callType} Call Recording`);
        setVideoPlayerOpen(true);
    };

    const handleCloseVideoPlayer = (): void => {
        setVideoPlayerOpen(false);
        setSelectedVideoUrl('');
        setSelectedVideoTitle('');
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, recording: CallRecording): void => {
        setAnchorEl(event.currentTarget);
        setSelectedRecording(recording);
    };

    const handleMenuClose = (): void => {
        setAnchorEl(null);
        setSelectedRecording(null);
    };

    const handlePlayFromMenu = (): void => {
        if (selectedRecording) {
            handlePlayRecording(
                selectedRecording.final_output_file,
                selectedRecording.room_id,
                selectedRecording.call_type
            );
        }
        handleMenuClose();
    };

    const handleDownloadRecording = (): void => {
        if (!selectedRecording) return;

        try {
            const link = document.createElement('a');
            link.href = selectedRecording.final_output_file;
            link.download = `recording_${selectedRecording.room_id}.mkv`;
            // link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success('Download started', {
                position: 'top-right',
                autoClose: 2000
            });
        } catch (error) {
            toast.error('Failed to download recording', {
                position: 'top-right',
                autoClose: 3000
            });
        }
        handleMenuClose();
    };

    const formatTime = (timeString: string): string => {
        return new Date(timeString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getCallStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'completed':
                return { bgcolor: '#e8f5e9', color: '#2e7d32' };
            case 'failed':
                return { bgcolor: '#ffebee', color: '#c62828' };
            case 'ongoing':
                return { bgcolor: '#e3f2fd', color: '#1565c0' };
            default:
                return { bgcolor: '#f5f5f5', color: '#666666' };
        }
    };

    const dateRangeOptions = [
        { value: 'last7days', label: 'Last 7 days' },
        { value: 'last14days', label: 'Last 14 days' },
        { value: 'last4weeks', label: 'Last 4 weeks' },
        { value: 'custom', label: 'Custom range' },
    ];

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <PageHeader
                    heading="Call Recordings"
                    subheading="Manage and review your call recordings here."
                    variant="gradient"
                />
            </Box>
            <Box sx={{ mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
                    <Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            {/* <Button
                                variant="contained"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={handleOpenReportDialog}
                                sx={{
                                    background: themeBackgrounds.default,
                                    color: '#ffffff',
                                    '&:hover': {
                                        background: themeBackgrounds.default,
                                        opacity: 0.9,
                                    },
                                }}
                            >
                                Download Report
                            </Button> */}
                        </Stack>
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                        <Box sx={{ minWidth: 200 }}>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={selectedApplicationId}
                                    onChange={(e) => {
                                        setSelectedApplicationId(e.target.value);
                                        setPage(0);
                                    }}
                                    displayEmpty
                                    sx={{
                                        '& .MuiSelect-select': {
                                            display: 'flex',
                                            alignItems: 'center',
                                        },
                                    }}
                                >
                                    {applications.map((app) => (
                                        <MenuItem key={app.appId} value={app.appId}>
                                            {app.appName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {applications.find(app => app.appId === selectedApplicationId)?.appName || 'Select Application'}
                            </Typography>
                        </Box>

                        <Box sx={{ minWidth: 200 }}>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={dateRange}
                                    onChange={(e) => handleDateRangeChange(e.target.value)}
                                    startAdornment={<CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                                    displayEmpty
                                    sx={{
                                        '& .MuiSelect-select': {
                                            display: 'flex',
                                            alignItems: 'center',
                                        },
                                    }}
                                >
                                    {dateRangeOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {getCurrentDateRange() && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    {getCurrentDateRange()}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </Stack>
            </Box>

            {/* Recordings Table */}
            <Paper>
                {isLoadingRecordings ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Loading call recordings...</Typography>
                    </Box>
                ) : (
                    <>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Room ID</TableCell>
                                    <TableCell>Call Start Time</TableCell>
                                    <TableCell>Duration</TableCell>
                                    <TableCell>Call Type</TableCell>
                                    <TableCell>Call Mode</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recordings.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No recordings found for the selected date range and application
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    recordings.map((recording) => {
                                        const statusColor = getCallStatusColor(recording.call_status);
                                        return (
                                            <TableRow key={recording._id}>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {recording.room_id}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatTime(recording.call_start_time)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {recording.call_duration}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                                        {recording.call_type}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                                        {recording.call_mode}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            px: 1,
                                                            py: 0.5,
                                                            borderRadius: 1,
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500,
                                                            ...statusColor
                                                        }}
                                                    >
                                                        {recording.call_status}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleMenuOpen(e, recording)}
                                                        sx={{
                                                            color: '#667eea',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(102, 126, 234, 0.08)',
                                                            },
                                                        }}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={paginationInfo?.total_records || 0}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 20, 50]}
                        />
                    </>
                )}
            </Paper>

            {/* Custom Date Dialog */}
            <Dialog
                open={customDateOpen}
                onClose={handleCustomDateCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Select Custom Date Range</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            label="From Date"
                            type="date"
                            value={tempFromDate ? tempFromDate.toISOString().split('T')[0] : ''}
                            onChange={(e) => setTempFromDate(e.target.value ? new Date(e.target.value) : null)}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                        <TextField
                            label="To Date"
                            type="date"
                            value={tempToDate ? tempToDate.toISOString().split('T')[0] : ''}
                            onChange={(e) => setTempToDate(e.target.value ? new Date(e.target.value) : null)}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                min: tempFromDate ? tempFromDate.toISOString().split('T')[0] : undefined,
                                max: new Date().toISOString().split('T')[0]
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCustomDateCancel} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCustomDateApply}
                        variant="contained"
                        disabled={!tempFromDate || !tempToDate}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handlePlayFromMenu}>
                    <PlayArrowIcon sx={{ mr: 1, fontSize: 20 }} />
                    Play
                </MenuItem>
                <MenuItem onClick={handleDownloadRecording}>
                    <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
                    Download
                </MenuItem>
            </Menu>

            {/* Video Player Modal */}
            <VideoPlayerModal
                open={videoPlayerOpen}
                onClose={handleCloseVideoPlayer}
                videoUrl={selectedVideoUrl}
                title={selectedVideoTitle}
                autoPlay={true}
                duration={0}
            />
        </Box>
    )

}

export default CallRecordingsScreen;